import { z } from 'zod'

import {
  AdditionalOption,
  BasicOption,
  OldAdditionalOption,
  ParkingType,
  PropertyType,
  RefundPolicy,
  RoomScheduleStatus,
  RoomStatus,
} from './room'

export const ZRoomId = z.number()

export const ZRoomPosition = z.object({
  lat: z.number(),
  lng: z.number(),
})

export const LongTermDiscount = z.object({
  weeks: z.number(),
  discountRate: z.number(),
  discountedUsingFee: z.number(),
})

export const EarlyCheckinDiscount = z.object({
  days: z.number(),
  discountAmount: z.number(),
})

export const RefundTerm = z.object({
  beforeCheckinDays: z.number(),
  cancelFeePercent: z.number().nullable(),
})

// TODO: User 쪽으로 분리 후 가져와서 사용
export const ZHost = z.object({
  uid: z.number(),
  picProfile: z.string(),
  name: z.string(),
  isSuperHost: z.boolean(),
  isCertificated: z.boolean(),
  introduction: z.string(),
})

export const ZReview = z.object({
  contractId: z.number(),
  name: z.string(),
  score: z.number(),
  review: z.string(),
  reviewTime: z.string(),
})

export const ZSimpleReviewCount = z.object({
  simpleReviewTemplateId: z.number(),
  simpleReviewTemplateCode: z.string(),
  content: z.string(),
  isForHost: z.boolean(),
  count: z.number(),
})

export const ZSimpleRoom = z.object({
  rid: z.number(),
  picMain: z.string(),
  roomName: z.string(),
})

export const ZStepCompletionStatus = z.object({
  spaceInfo: z.boolean(),
  feeInfo: z.boolean(),
  options: z.boolean(),
  description: z.boolean(),
  contractPolicy: z.boolean(),
})

export const ZHostRoomProgress = z.object({
  stepCompletionStatus: ZStepCompletionStatus,
  requiredFieldsCompleted: z.boolean(),
  roomStatus: z.nativeEnum(RoomStatus),
})

export const ZHostRoom = z.object({
  rid: ZRoomId, // 방 ID
  roomName: z.string(), // 방 이름
  addrLot: z.string(), // 지번 주소
  addrFloor: z.string(), // 층수
  addrDetail: z.string(), // 상세 주소
  usingFee: z.number(), // 1주 이용요금
  mainImageUrl: z.string(), // 방 대표 사진
  status: z.nativeEnum(RoomStatus), // 방 상태
  isPublic: z.boolean(), // 공개 여부
  requiredFieldsCompleted: z.boolean(), // 필수 필드 입력 완료 여부
  stepCompletionStatus: ZStepCompletionStatus, // 스텝별 작성 완료 여부
})

export const ZRoomPreview = z.object({
  rid: ZRoomId,
  roomName: z.string(),
  state: z.string(),
  province: z.string(),
  town: z.string(),
  picMain: z.string().nullable(),
  addrLot: z.string(),
  addrStreet: z.string(),
  propertyType: z.string(),
  usingFee: z.number(),
  mgmtFee: z.number(),
  pyeongSize: z.number(),
  roomCnt: z.number(),
  bathroomCnt: z.number(),
  cookroomCnt: z.number(),
  sittingroomCnt: z.number(),
  recoType1: z.boolean(),
  recoType2: z.boolean(),
  longtermDiscountPer: z.number(),
  earlyDiscountAmount: z.number(),
  isNew: z.boolean(),
  isSuperHost: z.boolean(),
  lat: z.number(),
  lng: z.number(),
  like: z.boolean(),
})

export const ZRoom = z
  .object({
    rid: ZRoomId,
    roomName: z.string(),
    propertyType: z.nativeEnum(PropertyType),
    hasElevator: z.boolean(),
    parkingType: z.nativeEnum(ParkingType),
    parkingNotice: z.string(),
    description: z.string(),
    additionalDescription: z.string(),
    usageGuide: z.string(),
    basicOptions: z.array(z.nativeEnum(BasicOption)),
    additionalOptions: z.array(z.nativeEnum(AdditionalOption)),
    petAllowed: z.boolean(),
    addrLot: z.string(),
    addrStreet: z.string(),
    addrDetail: z.string(),
    floor: z.string(),
    zipCode: z.string(),
    state: z.string(),
    province: z.string(),
    town: z.string(),
    subwayStations: z.array(z.string()),
    schools: z.array(z.string()),
    usingFee: z.number(),
    mgmtFee: z.number(),
    longTermDiscounts: z.array(LongTermDiscount),
    earlyCheckinDiscounts: z.array(EarlyCheckinDiscount),
    includeElectricity: z.boolean(),
    includeWater: z.boolean(),
    includeGas: z.boolean(),
    managementFeeDescription: z.string(),
    cleanFee: z.number(),
    refundPolicy: z.nativeEnum(RefundPolicy),
    roomCnt: z.number(),
    bathroomCnt: z.number(),
    cookroomCnt: z.number(),
    sittingroomCnt: z.number(),
    sharedRoom: z.boolean(),
    sharedBathroom: z.boolean(),
    sharedCookroom: z.boolean(),
    duplexStructure: z.boolean(),
    pyeongSize: z.number(),
    transportation: z.string(),
    minimumContractWeeks: z.number(),
  })
  .merge(ZRoomPosition)

export const ZRoomDetail = ZRoom.omit({
  addrDetail: true,
  zipCode: true,
  floor: true,
  schools: true,
}).extend({
  cid: z.number().nullable(),
  reviewScore: z.number(),
  missingOptions: z.array(
    z.union([z.nativeEnum(BasicOption), z.nativeEnum(AdditionalOption)]),
  ),
  deposit: z.number(),
  refundTerms: z.array(RefundTerm),
  squareMeterSize: z.number(),
  roomUrl: z.string().nullable(),
  pictures: z.array(z.string()),
  like: z.boolean(),
  hostUser: ZHost,
  reviewList: z.array(ZReview),
  simpleReviewCounts: z.array(ZSimpleReviewCount),
  otherHostRooms: z.array(ZSimpleRoom),
})

export const ZContractRoomDetail = ZRoom.omit({
  addrDetail: true,
  zipCode: true,
  floor: true,
  schools: true,
  additionalOptions: true,
}).extend({
  isOldRoomOptions: z.boolean(), // 방 옵션 변경 이전의 계약 여부 (true 인 경우 oldAddtionalOptions, includeInternet 사용)
  additionalOptions: z.array(z.nativeEnum(AdditionalOption)).nullable(), // additionalOptions 또는 oldAdditionalOptions 중 하나가 있고 하나는 null
  oldAdditionalOptions: z.array(z.nativeEnum(OldAdditionalOption)).nullable(), // additionalOptions 또는 oldAdditionalOptions 중 하나가 있고 하나는 null
  missingOptions: z.array(
    z.union([z.nativeEnum(BasicOption), z.nativeEnum(AdditionalOption)]),
  ),
  deposit: z.number(),
  includeInternet: z.boolean().nullable(),
  isOldRefundTerms: z.boolean(),
  refundTerms: z.array(RefundTerm),
  squareMeterSize: z.number(),
  pictures: z.array(z.string()),
})

export const ZFavoriteRoom = ZRoomDetail.pick({
  rid: true,
  roomName: true,
  addrLot: true,
  addrStreet: true,
  propertyType: true,
  usingFee: true,
  mgmtFee: true,
  pyeongSize: true,
  roomCnt: true,
  bathroomCnt: true,
  cookroomCnt: true,
  sittingroomCnt: true,
  like: true,
}).extend({
  picMain: z.string().nullable(),
  state: z.string(),
  province: z.string(),
  town: z.string(),
  recoType1: z.boolean(),
  recoType2: z.boolean(),
  longtermDiscountPer: z.number(),
  earlyDiscountAmount: z.number(),
  isNew: z.boolean(),
  isSuperHost: z.boolean(),
})

export const ZRecentViewedRoom = ZRoomDetail.pick({
  rid: true,
  roomName: true,
  addrLot: true,
  addrStreet: true,
  propertyType: true,
  usingFee: true,
  mgmtFee: true,
  pyeongSize: true,
  roomCnt: true,
  bathroomCnt: true,
  cookroomCnt: true,
  sittingroomCnt: true,
  like: true,
}).extend({
  picMain: z.string().nullable(),
  state: z.string(),
  province: z.string(),
  town: z.string(),
  recoType1: z.boolean(),
  recoType2: z.boolean(),
  longtermDiscountPer: z.number(),
  earlyDiscountAmount: z.number(),
  isNew: z.boolean(),
  isSuperHost: z.boolean(),
  viewDate: z.string(),
})

export const ZRoomContractSchedule = z.object({
  initialContractId: z.number(),
  startDate: z.string(), // 총 계약 시작일
  endDate: z.string(), // 총 계약 종료일
  checkoutDate: z.string().nullable(), // 퇴실일
})

export const ZRoomSchedule = z.object({
  date: z.string(),
  status: z.nativeEnum(RoomScheduleStatus),
})

export const ZRoomSchedules = z.object({
  contracts: z.array(ZRoomContractSchedule),
  schedules: z.array(ZRoomSchedule),
})

export const ZRoomAvailableWeeks = z.object({
  minAvailableWeeks: z.number(),
  maxAvailableWeeks: z.number(),
})

export const ZRoomSpaceInfo = z.object({
  propertyType: z.nativeEnum(PropertyType), // 건물 유형
  zipCode: z.string(), // 우편번호
  addrLot: z.string(), // 지번 주소
  addrStreet: z.string(), // 도로명 주소
  addrStreetEn: z.string(), // 영문 도로명 주소
  addrDetail: z.string(), // 상세 주소
  floor: z.string(), // 층수
  state: z.string(), // 시/도
  province: z.string(), // 시/군/구
  town: z.string(), // 읍/면/동
  subwayStations: z.array(z.string()), // 인접 지하철역
  schools: z.array(z.string()), // 인접 학교
  lat: z.number(), // 위도
  lng: z.number(), // 경도
  pyeongSize: z.number().int(), // 전용 면적(평수)
  roomCount: z.number().int(), // 방 개수
  bathroomCount: z.number().int(), // 화장실 개수
  kitchenCount: z.number().int(), // 주방 개수
  livingRoomCount: z.number().int(), // 거실 개수
  duplexStructure: z.boolean(), // 복층 구조 여부
  sharedRoom: z.boolean(), // 공용 방 여부
  sharedBathroom: z.boolean(), // 공용 화장실 여부
  sharedKitchen: z.boolean(), // 공용 주방 및 거실 여부
  hasElevator: z.boolean(), // 엘리베이터 유무
  parkingType: z.nativeEnum(ParkingType), // 주차 유형
  parkingNotice: z.string(), // 주차 안내(선택)
})

export const ZLongTermDiscount = z.object({
  weeks: z.number().int(), // 임대 기간 (주 단위)
  discountRate: z.number().int(), // 할인율 (백분율)
})

export const ZEarlyCheckinDiscount = z.object({
  days: z.number().int(), // 조기 예약 기간 (일 단위) - 0: 당일 입주
  discountAmount: z.number().int(), // 할인 금액 (만 원 단위)
})

export const ZRoomOptions = ZRoom.pick({
  basicOptions: true,
  additionalOptions: true,
  petAllowed: true,
})

export const ZRoomImage = z.object({
  index: z.number().int(), // 방 사진 인덱스 (대표 사진은 0번)
  objectKey: z.string(), // 방 사진 S3 객체 key
})

export const ZRoomDescription = z.object({
  roomName: z.string(), // 방 이름
  roomImages: z.array(ZRoomImage), // 방 사진 objectKey 목록
  description: z.string().nullable(), // 방 소개
  transportation: z.string().nullable(), // 교통편
  usageGuide: z.string().nullable(), // 이용 방법
  additionalDescription: z.string().nullable(), // 추가 설명
})

export const ZRoomFeeInfo = z.object({
  usingFee: z.number().int(), // 1주 임대료
  longTermDiscounts: z.array(ZLongTermDiscount), // 장기 계약 할인 정책
  earlyCheckinDiscounts: z.array(ZEarlyCheckinDiscount), // 즉시 입주 할인 정책
  managementFee: z.number().int(), // 1주 관리비
  includeElectricity: z.boolean(), // 관리비 포함 여부 - 전기
  includeWater: z.boolean(), // 관리비 포함 여부 - 수도
  includeGas: z.boolean(), // 관리비 포함 여부 - 가스
  managementFeeDescription: z.string().nullable(), // 관리비 상세 설명
  cleanFee: z.number().int(), // 퇴실 청소비
  refundPolicy: z.nativeEnum(RefundPolicy).nullable(), // 환불 정책
  propertyType: z.nativeEnum(PropertyType), // 건물 유형 - 임대료 스텝에서 보증금 표시에 필요한 필드
})

export const ZRoomContractPolicy = z.object({
  minimumContractWeeks: z.number().int(), // 최소 계약 기간(주)
  allowCheckinDayContract: z.boolean(), // 입주일에 앞 계약 받기 (입주일에 이전 계약 퇴실 허용)
  allowCheckoutDayContract: z.boolean(), // 퇴실일에 다음 계약 받기 (퇴실일에 다음 계약 입주 허용)
})

export const ZRoomRecommendSearchKeyword = z.object({
  keywords: z.array(z.string()),
})

export type TStepCompletionStatus = z.infer<typeof ZStepCompletionStatus>
export type THostRoomProgress = z.infer<typeof ZHostRoomProgress>

export type THostRoom = z.infer<typeof ZHostRoom>

export type TRoomId = z.infer<typeof ZRoomId>
export type TRoomPosition = z.infer<typeof ZRoomPosition>

export type TLongTermDiscount = z.infer<typeof LongTermDiscount>
export type TEarlyCheckinDiscount = z.infer<typeof EarlyCheckinDiscount>
export type TRefundTerm = z.infer<typeof RefundTerm>
export type THost = z.infer<typeof ZHost>
export type TReview = z.infer<typeof ZReview>
export type TSimpleReviewCount = z.infer<typeof ZSimpleReviewCount>
export type TSimpleRoom = z.infer<typeof ZSimpleRoom>

export type TRoomPreview = z.infer<typeof ZRoomPreview>
export type TRoom = z.infer<typeof ZRoom>
export type TRoomDetail = z.infer<typeof ZRoomDetail>
export type TContractRoomDetail = z.infer<typeof ZContractRoomDetail>
export type TFavoriteRoom = z.infer<typeof ZFavoriteRoom>
export type TRecentViewedRoom = z.infer<typeof ZRecentViewedRoom>

export type TRoomContractSchedule = z.infer<typeof ZRoomContractSchedule>
export type TRoomSchedule = z.infer<typeof ZRoomSchedule>
export type TRoomSchedules = z.infer<typeof ZRoomSchedules>
export type TRoomAvailableWeeks = z.infer<typeof ZRoomAvailableWeeks>

export type TRoomSpaceInfo = z.infer<typeof ZRoomSpaceInfo>
export type TRoomOptions = z.infer<typeof ZRoomOptions>
export type TRoomImage = z.infer<typeof ZRoomImage>
export type TRoomDescription = z.infer<typeof ZRoomDescription>
export type TRoomFeeInfo = z.infer<typeof ZRoomFeeInfo>
export type TRoomContractPolicy = z.infer<typeof ZRoomContractPolicy>

export type TRoomRecommendSearchKeyword = z.infer<
  typeof ZRoomRecommendSearchKeyword
>
